import React, { useState, useEffect } from 'react';
import { settingsApi, emailApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, Loader2, Clock, AlertTriangle, Zap, Package, Truck, Mail, Send, FileText, MessageSquare } from 'lucide-react';

const Settings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSaving, setEmailSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [dtTemplates, setDtTemplates] = useState([]);
  const [dtLoading, setDtLoading] = useState(false);
  const [dtError, setDtError] = useState(null);          // Surface DoubleTick API errors
  const [dtManualEntry, setDtManualEntry] = useState(false); // Fall back to text input when listing isn't available

  useEffect(() => {
    fetchSettings();
    fetchEmailPrefs();
    fetchDtTemplates();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailPrefs = async () => {
    setEmailLoading(true);
    try {
      const res = await emailApi.getPreferences();
      setEmailPrefs(res.data);
    } catch { /* ignore if not set */ }
    finally { setEmailLoading(false); }
  };

  const fetchDtTemplates = async () => {
    setDtLoading(true);
    setDtError(null);
    try {
      const res = await settingsApi.getWaTemplates();
      // Support both old (array) and new ({templates, error, manual_entry_required}) shapes
      if (Array.isArray(res.data)) {
        setDtTemplates(res.data);
        setDtManualEntry(res.data.length === 0);
        setDtError(res.data.length === 0 ? 'Templates list is empty. Check DoubleTick configuration.' : null);
      } else {
        setDtTemplates(res.data?.templates || []);
        setDtError(res.data?.error || null);
        setDtManualEntry(!!res.data?.manual_entry_required);
      }
    } catch (e) {
      setDtError(e?.response?.data?.detail || 'Failed to reach the templates endpoint.');
      setDtManualEntry(true);
    } finally {
      setDtLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailPrefToggle = async (key, val) => {
    const updated = { ...emailPrefs, [key]: val };
    setEmailPrefs(updated);
    setEmailSaving(true);
    try {
      await emailApi.updatePreferences(updated);
      toast.success('Email preferences updated');
    } catch { toast.error('Failed to update'); }
    finally { setEmailSaving(false); }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      await emailApi.sendDailyReport();
      toast.success('Daily report sent!');
    } catch { toast.error('Failed to send report'); }
    finally { setSendingReport(false); }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const res = await emailApi.sendTest();
      toast.success(`Test email sent to ${res.data.sent_to}`);
    } catch { toast.error('Failed to send test email'); }
    finally { setSendingTest(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header title="Settings" />
        <div className="p-6">
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" data-testid="settings-page">
      <Header title="Settings">
        <Button 
          onClick={handleSave}
          data-testid="save-settings-btn"
          className="bg-amber-500 hover:bg-amber-600"
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </Header>

      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4 md:space-y-6">
        {/* Material Issuance Rules */}
        <Card className="border-amber-200 dark:border-amber-500/30">
          <CardHeader className="pb-3 md:pb-6">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 md:h-10 md:w-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Package className="h-4 w-4 md:h-5 md:w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <CardTitle className="font-heading text-base md:text-lg">Material Issuance Rules</CardTitle>
                <CardDescription className="text-xs md:text-sm">Configure material issuance timing</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 md:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-2">
                <Label htmlFor="material_issue_days" className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-amber-500" />
                  Issue Materials After (days)
                </Label>
                <Input
                  id="material_issue_days"
                  type="number"
                  data-testid="material-issue-days-input"
                  value={settings?.material_issue_days || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    material_issue_days: parseInt(e.target.value) || 0
                  })}
                  placeholder="2"
                />
                <p className="text-[10px] md:text-xs text-slate-500">
                  Alerts appear if materials aren't issued within this many days
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="material_warning_days" className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Warning Before Deadline (days)
                </Label>
                <Input
                  id="material_warning_days"
                  type="number"
                  data-testid="material-warning-days-input"
                  value={settings?.material_warning_days || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    material_warning_days: parseInt(e.target.value) || 0
                  })}
                  placeholder="1"
                />
                <p className="text-[10px] md:text-xs text-slate-500">
                  Show warning this many days before deadline
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Factory Delivery Rules - NEW */}
        <Card className="border-emerald-200 dark:border-emerald-500/30">
          <CardHeader className="pb-3 md:pb-6">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 md:h-10 md:w-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Truck className="h-4 w-4 md:h-5 md:w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <CardTitle className="font-heading text-base md:text-lg">Factory Delivery Rules</CardTitle>
                <CardDescription className="text-xs md:text-sm">Configure factory delivery timelines</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 md:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="casting_to_factory_days" className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-emerald-500" />
                Casting to Factory Delivery (days)
              </Label>
              <Input
                id="casting_to_factory_days"
                type="number"
                data-testid="casting-to-factory-days-input"
                value={settings?.casting_to_factory_days || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  casting_to_factory_days: parseInt(e.target.value) || 0
                })}
                placeholder="7"
              />
              <p className="text-[10px] md:text-xs text-slate-500">
                When pieces move to "Casting", the "Expected from Factory" date will be set to this many days from now.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Business Rules */}
        <Card>
          <CardHeader className="pb-3 md:pb-6">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 md:h-10 md:w-10 rounded-xl bg-slate-100 dark:bg-slate-500/20 flex items-center justify-center flex-shrink-0">
                <SettingsIcon className="h-4 w-4 md:h-5 md:w-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <CardTitle className="font-heading text-base md:text-lg">Production Timeline</CardTitle>
                <CardDescription className="text-xs md:text-sm">Configure timeline rules</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 md:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-2">
                <Label htmlFor="avg_duration" className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Avg Order Duration (days)
                </Label>
                <Input
                  id="avg_duration"
                  type="number"
                  data-testid="avg-duration-input"
                  value={settings?.avg_order_duration_days || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    avg_order_duration_days: parseInt(e.target.value) || 0
                  })}
                  placeholder="28"
                />
                <p className="text-[10px] md:text-xs text-slate-500">
                  Expected time from order creation to shipment
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="factory_turnaround" className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Factory Turnaround (days)
                </Label>
                <Input
                  id="factory_turnaround"
                  type="number"
                  data-testid="factory-turnaround-input"
                  value={settings?.factory_turnaround_days || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    factory_turnaround_days: parseInt(e.target.value) || 0
                  })}
                  placeholder="10"
                />
                <p className="text-[10px] md:text-xs text-slate-500">
                  Expected factory processing time
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Priority Settings */}
        <Card>
          <CardHeader className="pb-3 md:pb-6">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 md:h-10 md:w-10 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <CardTitle className="font-heading text-base md:text-lg">Priority Settings</CardTitle>
                <CardDescription className="text-xs md:text-sm">Configure priority deadlines</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 md:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-2">
                <Label htmlFor="urgent_days" className="flex items-center gap-2 text-sm">
                  <Zap className="h-4 w-4 text-red-500" />
                  Urgent Priority Window (days)
                </Label>
                <Input
                  id="urgent_days"
                  type="number"
                  data-testid="urgent-days-input"
                  value={settings?.urgent_priority_days || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    urgent_priority_days: parseInt(e.target.value) || 0
                  })}
                  placeholder="7"
                />
                <p className="text-[10px] md:text-xs text-slate-500">
                  Orders due within this period are urgent
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="high_days" className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  High Priority Window (days)
                </Label>
                <Input
                  id="high_days"
                  type="number"
                  data-testid="high-days-input"
                  value={settings?.high_priority_days || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    high_priority_days: parseInt(e.target.value) || 0
                  })}
                  placeholder="14"
                />
                <p className="text-[10px] md:text-xs text-slate-500">
                  Orders due within this period are high priority
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Notifications */}
        <Card className="border-blue-200 dark:border-blue-500/30">
          <CardHeader className="pb-3 md:pb-6">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 md:h-10 md:w-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <Mail className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="font-heading text-base md:text-lg">Email Notifications</CardTitle>
                <CardDescription className="text-xs md:text-sm">Choose which emails you receive</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {emailLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : emailPrefs ? (
              <>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Email Notifications</p>
                    <p className="text-xs text-muted-foreground">Master toggle for all email notifications</p>
                  </div>
                  <Switch
                    checked={emailPrefs.enabled}
                    onCheckedChange={(v) => handleEmailPrefToggle('enabled', v)}
                    data-testid="email-enabled-toggle"
                  />
                </div>
                <Separator />
                {[
                  { key: 'new_order', label: 'New Order Created', desc: 'When a new production order is created' },
                  { key: 'dispatched', label: 'Order Dispatched', desc: 'When an order is fully dispatched' },
                  { key: 'overdue', label: 'Overdue Alerts', desc: 'When orders pass their delivery date' },
                  { key: 'factory_concern', label: 'Factory Concerns', desc: 'When a factory raises a concern' },
                  { key: 'daily_report', label: 'Daily Morning Report', desc: 'Dashboard summary sent every morning' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between py-1.5">
                    <div>
                      <p className="text-sm font-medium text-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                    <Switch
                      checked={emailPrefs[key] ?? true}
                      onCheckedChange={(v) => handleEmailPrefToggle(key, v)}
                      disabled={!emailPrefs.enabled}
                      data-testid={`email-pref-${key}`}
                    />
                  </div>
                ))}
                <Separator />
                <div className="flex gap-2 pt-2">
                  {user?.role === 'admin' && (
                    <Button variant="outline" size="sm" onClick={handleSendReport} disabled={sendingReport} data-testid="send-daily-report-btn">
                      {sendingReport ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                      Send Daily Report Now
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={handleSendTest} disabled={sendingTest} data-testid="send-test-email-btn">
                    {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Send Test Email
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Unable to load email preferences</p>
            )}
          </CardContent>
        </Card>

        {/* WhatsApp Message Templates */}
        <Card className="border-emerald-200 dark:border-emerald-500/30">
          <CardHeader className="pb-3 md:pb-6">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 md:h-10 md:w-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="h-4 w-4 md:h-5 md:w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <CardTitle className="font-heading text-base md:text-lg">WhatsApp Message Templates</CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  Select a DoubleTick template and customize the message body for each notification.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* DoubleTick API status banner — shown when templates list couldn't be fetched */}
            {dtError && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 flex items-start gap-2" data-testid="dt-templates-error">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[11px] md:text-xs font-semibold text-amber-800 dark:text-amber-300">DoubleTick template list unavailable</p>
                  <p className="text-[10px] md:text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">{dtError}</p>
                  {dtManualEntry && (
                    <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1">
                      Each template-name field has been switched to a manual text input. Type the exact template name (the name configured in your DoubleTick dashboard, e.g. <code className="font-mono">siddhi_order_created</code>) and save.
                    </p>
                  )}
                  <button
                    onClick={fetchDtTemplates}
                    className="text-[10px] mt-1 underline text-amber-800 dark:text-amber-300 hover:text-amber-900"
                    data-testid="dt-templates-retry"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
              <p className="text-[10px] md:text-xs font-semibold text-emerald-800 dark:text-emerald-300 mb-1.5">Available Variables for Message Body</p>
              <div className="flex flex-wrap gap-1.5">
                {['customer_name', 'order_number', 'pieces', 'status', 'salesperson', 'delivery_date', 'factory', 'ref_number', 'logistics', 'tracking', 'message'].map(v => (
                  <code key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-white dark:bg-neutral-800 border text-emerald-700 dark:text-emerald-400 font-mono">{`{{${v}}}`}</code>
                ))}
              </div>
            </div>

            {[
              { key: 'order_created', label: 'Order Created', desc: 'Sent automatically when a new order is created (if customer phone is set)', dtKey: 'wa_dt_name_order_created', tplKey: 'wa_template_order_created', placeholder: "Your order *{{order_number}}* has been placed with Siddhi Jewels.\nPieces: {{pieces}}\n\nWe'll keep you updated. Thank you!", rows: 4 },
              { key: 'order_dispatched', label: 'Order Dispatched', desc: 'Sent automatically when a dispatch is recorded (if customer phone is set)', dtKey: 'wa_dt_name_order_dispatched', tplKey: 'wa_template_order_dispatched', placeholder: "Your order *{{order_number}}* — {{pieces}} pcs has been dispatched.\nRef: {{ref_number}}\nLogistics: {{logistics}}\nTracking: {{tracking}}\n\nThank you!", rows: 4 },
              { key: 'manual_update', label: 'Manual Update', desc: 'Format for the "WhatsApp Update" button on order detail. Use {{message}} for the typed message.', dtKey: 'wa_dt_name_manual_update', tplKey: 'wa_template_manual_update', placeholder: "{{message}}", rows: 3 },
              { key: 'chain_po_sent', label: 'Chain PO — Sent', desc: 'Sent to the chain vendor when a Chain PO is marked as sent. Variables (in template order): {{1}}=vendor name, {{2}}=PO number, {{3}}=PO date, {{4}}=items block (multi-line: chain / karat / qty), {{5}}=total pcs, {{6}}=expected date.', dtKey: 'wa_dt_name_chain_po_sent', tplKey: 'wa_template_chain_po_sent', placeholder: "Hi {{1}}, new PO {{2}} dated {{3}}.\nItems:\n{{4}}\nTotal: {{5}} pcs. Expected by {{6}}. Please confirm.", rows: 6 },
              { key: 'chain_po_partial', label: 'Chain PO — Partial Receipt', desc: 'Fires when we receive a partial delivery. Variables: {{1}}=vendor, {{2}}=PO number, {{3}}=items block with progress per line (received/ordered), {{4}}=received now, {{5}}=total received so far, {{6}}=still pending, {{7}}=total ordered.', dtKey: 'wa_dt_name_chain_po_partial', tplKey: 'wa_template_chain_po_partial', placeholder: "{{1}} — PO {{2}} partial receipt.\n{{3}}\nReceived now: {{4}}, so far: {{5}}/{{7}}, pending: {{6}}.", rows: 6 },
              { key: 'chain_po_fully_received', label: 'Chain PO — Fully Received', desc: 'Fires when the last piece for a PO lands. Variables: {{1}}=vendor, {{2}}=PO number, {{3}}=items block (final, per line), {{4}}=total received.', dtKey: 'wa_dt_name_chain_po_fully_received', tplKey: 'wa_template_chain_po_fully_received', placeholder: "PO {{2}} fully received — {{4}} pcs.\n{{3}}\nThanks {{1}}!", rows: 5 },
              { key: 'chain_po_closed', label: 'Chain PO — Closed', desc: 'Fires when the PO is closed. Variables: {{1}}=vendor, {{2}}=PO number.', dtKey: 'wa_dt_name_chain_po_closed', tplKey: 'wa_template_chain_po_closed', placeholder: "{{1}} — PO {{2}} closed. All settled.", rows: 2 },
              { key: 'chain_po_reminder', label: 'Chain PO — Overdue Reminder', desc: 'Manual reminder from the PO detail dialog. Variables: {{1}}=vendor, {{2}}=PO number, {{3}}=items block with progress per line, {{4}}=pending qty, {{5}}=total ordered, {{6}}=expected date.', dtKey: 'wa_dt_name_chain_po_reminder', tplKey: 'wa_template_chain_po_reminder', placeholder: "Hi {{1}}, reminder — PO {{2}}:\n{{3}}\nStill owes {{4}}/{{5}} pcs. Expected by {{6}}.", rows: 6 },
              { key: 'chain_hooks_issued', label: 'Chain — Spring Hooks Issued', desc: 'Fires when spring hooks are issued to a chain vendor. Variables: {{1}}=vendor, {{2}}=qty, {{3}}=date.', dtKey: 'wa_dt_name_chain_hooks_issued', tplKey: 'wa_template_chain_hooks_issued', placeholder: "{{1}} — issued {{2}} spring hooks on {{3}}.", rows: 2 },
              { key: 'exhibition_selection_en', label: 'Exhibition Selection — English', desc: 'Fires when a booth selection is finalised in English. Variables: {{1}}=contact name, {{2}}=exhibition name, {{3}}=sales staff. Header: PDF attachment.', dtKey: 'wa_dt_name_exhibition_selection_en', tplKey: 'wa_template_exhibition_selection_en', placeholder: "Hi {{1}},\n\nThank you for visiting Siddhi Jewels at {{2}}. Attached is your exclusive selection from your visit today.\n\nWarm regards,\n{{3}}\nSiddhi Jewels", rows: 6 },
              { key: 'exhibition_selection_ml', label: 'Exhibition Selection — Malayalam', desc: 'Same variables, Malayalam translation. Register in DoubleTick console.', dtKey: 'wa_dt_name_exhibition_selection_ml', tplKey: 'wa_template_exhibition_selection_ml', placeholder: "പ്രിയ {{1}}, {{2}} സന്ദർശിച്ചതിന് നന്ദി. നിങ്ങളുടെ selection PDF ചേർത്തിരിക്കുന്നു.\n\n{{3}}\nSiddhi Jewels", rows: 6 },
              { key: 'exhibition_selection_ta', label: 'Exhibition Selection — Tamil', desc: 'Same variables, Tamil translation. Register in DoubleTick console.', dtKey: 'wa_dt_name_exhibition_selection_ta', tplKey: 'wa_template_exhibition_selection_ta', placeholder: "வணக்கம் {{1}}, {{2}} இல் எங்களை பார்வையிட்டமைக்கு நன்றி. உங்கள் selection PDF இணைத்துள்ளோம்.\n\n{{3}}\nSiddhi Jewels", rows: 6 },
            ].map(({ key, label, desc, dtKey, tplKey, placeholder, rows }, idx) => (
              <div key={key}>
                {idx > 0 && <Separator className="mb-5" />}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">{label}</Label>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">DoubleTick Template</Label>
                    {dtManualEntry ? (
                      // Manual text entry fallback — used when DoubleTick won't return the templates list
                      // (e.g. 403 FORBIDDEN on send-only API keys)
                      <Input
                        value={settings?.[dtKey] || ''}
                        onChange={(e) => setSettings({...settings, [dtKey]: e.target.value})}
                        placeholder="e.g. siddhi_order_created"
                        className="font-mono text-xs"
                        data-testid={`wa-dt-input-${key}`}
                      />
                    ) : (
                      <Select
                        value={settings?.[dtKey] || ''}
                        onValueChange={(v) => setSettings({...settings, [dtKey]: v})}
                      >
                        <SelectTrigger className="font-mono text-xs" data-testid={`wa-dt-select-${key}`}>
                          <SelectValue placeholder={dtLoading ? "Loading templates..." : "Select a DoubleTick template"} />
                        </SelectTrigger>
                        <SelectContent>
                          {dtTemplates.filter(t => t.placeholders > 0 || (t.variables && t.variables.length > 0)).map(t => (
                            <SelectItem key={t.name} value={t.name}>
                              <span className="font-mono text-xs">{t.name}</span>
                              <span className={`ml-2 text-[9px] px-1.5 py-0.5 rounded-full ${t.category === 'UTILITY' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' : 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400'}`}>
                                {t.category} ({t.variables?.length || t.placeholders} vars)
                              </span>
                            </SelectItem>
                          ))}
                          {dtTemplates.filter(t => t.placeholders === 0 && (!t.variables || t.variables.length === 0)).length > 0 && (
                            <>
                              {dtTemplates.filter(t => t.placeholders === 0 && (!t.variables || t.variables.length === 0)).map(t => (
                                <SelectItem key={t.name} value={t.name}>
                                  <span className="font-mono text-xs text-muted-foreground">{t.name}</span>
                                  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">{t.category} (static)</span>
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    {settings?.[dtKey] && (() => {
                      const tpl = dtTemplates.find(t => t.name === settings[dtKey]);
                      return tpl ? (
                        <div className="space-y-2">
                          <div className="p-2 rounded bg-muted/40 text-[10px] font-mono text-muted-foreground whitespace-pre-wrap">{tpl.body}</div>
                          {tpl.variables && tpl.variables.length > 0 && (
                            <div className="p-2 rounded bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20">
                              <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-400 mb-1">Variable Mapping (auto-filled from order data):</p>
                              <div className="flex flex-wrap gap-1">
                                {tpl.variables.map((v, i) => (
                                  <code key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white dark:bg-neutral-800 border font-mono text-blue-600 dark:text-blue-400">{`{{${v}}}`}</code>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Message Body (fills template placeholders)</Label>
                    <Textarea
                      value={settings?.[tplKey] || ''}
                      onChange={(e) => setSettings({...settings, [tplKey]: e.target.value})}
                      placeholder={placeholder}
                      rows={rows}
                      className="font-mono text-xs"
                      data-testid={`wa-tpl-${key}`}
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30">
          <CardContent className="p-4 md:p-6">
            <div className="flex gap-3 md:gap-4">
              <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <SettingsIcon className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm md:text-base text-blue-900 dark:text-blue-100 mb-1">
                  About Business Rules
                </h4>
                <p className="text-xs md:text-sm text-blue-700 dark:text-blue-300">
                  These settings help the system automatically detect delays and prioritize orders.
                  The ticker at the bottom shows real-time alerts for materials that need to be issued.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
